import { isQuestion, matchChoice, type Choice } from '@/speech/understand/match';

/**
 * What the game does with one thing the child said in answer to a menu (docs/design/
 * voice-control.md): the rules first, then the classifier, and a check-back when the classifier
 * leans one way without being sure. A wrong guess costs a question, never a wrong action.
 */

/** An option of a voice menu: its id, the words that pick it (rules), and its label (classifier). */
export interface MenuOption extends Choice {
  label: string;
}

export interface Menu {
  /** What the game asked, as the classifier reads it ("Which hair do you want?"). */
  question: string;
  options: readonly MenuOption[];
}

export type Decision =
  /** Act on this option. */
  | { kind: 'pick'; id: string; by: 'rules' | 'model'; p?: number }
  /** Probably this option: ask "did you mean ...?" first. */
  | { kind: 'confirm'; id: string; p: number }
  /** The child asked something: answer it (topics), then ask the menu again. */
  | { kind: 'question' }
  /** Nothing usable: ask again, naming the options. */
  | { kind: 'unclear' };

/**
 * The classifier acts at or above this probability, unless the answer is hedged (below). On the
 * voice menus' independent test, acting on every confident pick was wrong 3.7 points more often
 * than the rules, mostly on corrections and negations ("brown um no dark brown", "not face one");
 * checking those back instead keeps wrong actions at the rules' rate (voice-control.md).
 */
export const ACT_AT = 0.95;
/** Between this and ACT_AT, or when hedged, its pick is checked back with the child. */
export const CONFIRM_AT = 0.6;

/** The classifier: which of `labels` the child meant, or null while it is not loaded. */
export type Rank = (
  question: string,
  labels: readonly string[],
  text: string,
) => Promise<{ index: number | null; p: number } | null>;

export async function decide(text: string, menu: Menu, rank?: Rank): Promise<Decision> {
  const byRule = matchChoice(text, menu.options);
  if (byRule) return { kind: 'pick', id: byRule, by: 'rules' };
  if (isQuestion(text)) return { kind: 'question' };
  const ranked = rank
    ? await rank(
        menu.question,
        menu.options.map((o) => o.label),
        text,
      ).catch(() => null)
    : null;
  const option = ranked && ranked.index !== null ? menu.options[ranked.index] : undefined;
  if (!ranked || !option) return { kind: 'unclear' };
  if (ranked.p >= ACT_AT && !hedged(text))
    return { kind: 'pick', id: option.id, by: 'model', p: ranked.p };
  if (ranked.p >= CONFIRM_AT) return { kind: 'confirm', id: option.id, p: ranked.p };
  return { kind: 'unclear' };
}

/**
 * Words that make a pick unsafe to act on without asking: a negation or self-correction ("not
 * the cap", "brown um no dark brown"), a hedge ("or", "maybe", "actually"). The classifier's
 * confident picks on such answers were its main source of wrong actions.
 */
const HEDGES = new Set([
  'no',
  'not',
  'nope',
  'never',
  "don't",
  'dont',
  "didn't",
  'instead',
  'actually',
  'wait',
  'or',
  'but',
  'maybe',
  'except',
  'without',
]);

export function hedged(text: string): boolean {
  return text
    .toLowerCase()
    .replace(/[^a-z' ]/g, ' ')
    .split(/\s+/)
    .some((w) => HEDGES.has(w));
}

/** The check-back's own menu: yes or no. */
export const YES_NO: Menu = {
  question: 'Is that what you meant: yes or no?',
  options: [
    {
      id: 'yes',
      label: 'Yes',
      says: ['yes', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'right', 'correct', 'uh huh'],
    },
    { id: 'no', label: 'No', says: ['no', 'nope', 'nah', 'wrong', 'not that', 'uh uh'] },
  ],
};
